import React from "react";

import { flexRender, getCoreRowModel, useReactTable, getPaginationRowModel, getSortedRowModel, getFilteredRowModel } from "@tanstack/react-table";
import { Search } from "lucide-react";
import { ChevronLeft } from "lucide-react";
import { ChevronRight } from "lucide-react";
import { FolderClock } from "lucide-react";

import { Input } from "@/components/Common/Input/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/Common/Input/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/Common/Table/table";
import { Button2 } from "@/components/Common/button2";

export function DataTable({ columns, data, filterColumn = "country" }) {
  const [sorting, setSorting] = React.useState([]);
  const [filtering, setFiltering] = React.useState("");
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const table = useReactTable({
    data: Array.isArray(data) ? data : [],
    columns: Array.isArray(columns) ? columns : [],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setFiltering,
    state: {
      sorting,
      globalFilter: filtering,
      pagination,
    },
    globalFilterFn: (row, columnId, filterValue) => {
      return Object.values(row.original).some((value) => String(value).toLowerCase().includes(filterValue.toLowerCase()));
    },
  });

  const renderPagination = () => {
    const pageIndex = table.getState().pagination.pageIndex;
    const totalPages = table.getPageCount();
    const maxVisibleButtons = 5;

    const startPage = Math.max(0, pageIndex - Math.floor(maxVisibleButtons / 2));
    const endPage = Math.min(totalPages, startPage + maxVisibleButtons);

    return (
      <>
        {Array.from({ length: endPage - startPage }, (_, i) => startPage + i).map((page) => (
          <Button2
            key={page}
            variant={pageIndex === page ? "default" : "outline"}
            size="sm"
            className={pageIndex === page ? "bg-blue-600" : "bg-white"}
            onClick={() => table.setPageIndex(page)}
          >
            {page + 1}
          </Button2>
        ))}
      </>
    );
  };

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-[50vh]">
        <FolderClock size={40} className="text-black/50" />
        <p className="text-black/50 text-[1.3rem] font-semibold">No Data Available!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3">
            <Search className="h-4 w-4 text-gray-500" />
          </span>
          <Input placeholder={`Search...`} value={filtering} onChange={(e) => setFiltering(e.target.value.toLowerCase())} className="max-w-sm pl-10" />
        </div>
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table className="min-w-full">
          <TableHeader className="bg-gray-200">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={`px-6 py-3 text-left text-[1.1em] font-semibold ${header.column.id === "status" ? "sticky right-0 bg-[#eaeaea] shadow-md" : ""}`}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort() ? "cursor-pointer select-none" : "",
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: " 🔼",
                          desc: " 🔽",
                        }[header.column.getIsSorted()] ?? null}
                      </div>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className={`px-6 py-3 ${cell.column.id === "status" ? "sticky right-0 bg-[#FAFAFA] shadow-md" : ""}`}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-5">
          <div className="flex-1 text-sm text-muted-foreground">
            {table.getFilteredRowModel().rows.length > 0 ? (
              <>
                Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{" "}
                {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, table.getFilteredRowModel().rows.length)} of{" "}
                {table.getFilteredRowModel().rows.length} results
              </>
            ) : (
              "No results"
            )}
          </div>
          <Select
            value={table.getState().pagination.pageSize.toString()}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className="w-fit">
              <SelectValue placeholder="Select rows per page" />
            </SelectTrigger>
            <SelectContent>
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={pageSize.toString()}>
                  {pageSize} rows
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center space-x-2">
          <Button2 variant="outline" size="sm" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
            <ChevronLeft size={16} />
          </Button2>
          {renderPagination()}
          <Button2 variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
            <ChevronRight size={16} />
          </Button2>
        </div>
      </div>
    </div>
  );
}
